import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { getAuthHeaders } from '../../lib/api';
import { PASS_THROUGH_COSTS } from '../../lib/billing-copy';
import { useAuth } from '../../contexts/AuthContext';

export default function Subscribe() {
  const { user, userProfile, loading: authLoading, profileReady } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading || !profileReady) return;
    if (user && userProfile?.onboarding_completed_at === null) {
      navigate('/register', { replace: true });
      return;
    }
    const status = userProfile?.subscription_status;
    if (user && (status === 'active' || status === 'trialing')) {
      navigate('/client-management', { replace: true });
    }
  }, [user, userProfile, authLoading, profileReady, navigate]);

  async function handleSubscribe() {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <>
      <Helmet>
        <title>Subscribe — Nurture Buddy</title>
      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Logo / brand */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Icon name="MessageCircle" size={28} className="text-primary" />
            <span className="text-xl font-bold text-foreground">Nurture Buddy</span>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-foreground mb-2 text-center">
              Start your subscription
            </h1>
            <p className="text-sm text-muted-foreground text-center mb-8">
              A subscription is required to send touchpoints and messages.
            </p>

            {/* Pricing */}
            <div className="bg-muted/50 rounded-xl p-5 mb-6">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-2xl font-bold text-foreground">$297</span>
                <span className="text-sm text-muted-foreground">/ month</span>
              </div>
              <p className="text-xs text-muted-foreground mb-2">Plus usage &amp; pass-through costs on your Stripe invoice:</p>
              <ul className="space-y-1.5 text-xs text-muted-foreground mb-4">
                {PASS_THROUGH_COSTS.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-muted-foreground/70 mt-0.5">•</span>
                    {item}
                  </li>
                ))}
              </ul>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {[
                  'AI-generated evergreen touchpoints',
                  'Automated SMS drip delivery',
                  'A2P 10DLC compliance registration',
                  'Local area code number matching',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Icon name="Check" size={14} className="text-green-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive mb-4">
                <Icon name="AlertCircle" size={15} />
                {error}
              </div>
            )}

            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Icon name="Loader2" size={16} className="animate-spin" />
              ) : (
                <Icon name="CreditCard" size={16} />
              )}
              {loading ? 'Loading…' : 'Subscribe with Stripe'}
            </button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Secured by Stripe. Cancel anytime from Settings.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
